import React from "react";
import { Badge, Box, Flex, HStack, Text } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { usePartnerProgram } from "../../contexts/PartnerProgramContext";
import { PARTNER_ACCOUNT_MENU, isPartnerMenuActive, partnerMenuFor, showsCreatorHomeDoor } from "../../lib/partnerNav";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { partnerStageBadge, partnerTypeBadge } from "../../lib/partnerHub";
import { PARTNER_STRIP_H } from "./PartnerTopStrip";
import { tt } from "../../i18n/tt";

// 파트너 포털 좌측 레일 (2026-09-10 N3, PC 전용 — md 아래에서는 BottomNavBar 파트너 탭이 맡는다)
//   위: 상태 카드(이름 · 유형 · 상태 · 코드) — 값은 PartnerProgramContext(허브가 RPC 로 받은 최신 행)
//   중: 메뉴 — 유형별(partnerMenuFor · src/lib/partnerNav.js): 홈 · 가이드 · 권리사 영업(영상권리사) · 유치 현황(크리에이터) · 정산 (2026-09-11). 「계정」 구획은 없다 — R2(대표 선택 2026-09-10): 상태 카드를 누르면 내 정보(/edit-profile),
//       계정 항목은 상단 프로필 메뉴(PartnerTopStrip)가 맡는다. 화면 안 목차(프로필·보안·언어)와 중복되지 않게.
//   아래: 담당자 카드
// 🔒 다른 파트너의 정보·비율·유치 수는 어디에도 두지 않는다 (기획 §8 비밀유지).
//    유형은 확정(보라) · 선언만 「(확인 전)」(회색) · 선언 전 「유형 선언 전」(partnerTypeBadge), 코드는 활성·정지 상태에만 보인다 (허브 화면과 같은 규칙).

export const PARTNER_RAIL_W = 220;

function SectionLabel({ children }) {
  return (
    <Text fontSize="11px" fontWeight="700" color="neutral.400" letterSpacing="0.06em" px={3} pt={3} pb={1}>
      {children}
    </Text>
  );
}

function RailItem({ item, active, onClick }) {
  const Icon = item.icon;
  return (
    <Flex
      as="button" type="button" w="100%" textAlign="left"
      align="center" gap={2.5} px={3} py={2} borderRadius="8px"
      bg={active ? "brand.50" : "transparent"}
      color={active ? "brand.700" : "neutral.600"}
      fontWeight={active ? "700" : "500"} fontSize="sm"
      transition="all 0.15s ease"
      _hover={{ bg: active ? "brand.50" : "neutral.50", color: active ? "brand.700" : "neutral.800" }}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
    >
      <Icon size={17} />
      <Text as="span">{tt(item.labelKey, item.labelKo)}</Text>
    </Flex>
  );
}

export default function PartnerRail() {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const ctx = usePartnerProgram();
  const program = ctx?.program || null;

  const typeBadge = partnerTypeBadge(program);
  const menu = partnerMenuFor(program);
  const stageMeta = program ? partnerStageBadge(program.status) : null; // 서명 전은 「둘러보기」(2026-09-11)
  const showCode = Boolean(program && ["active", "suspended"].includes(program.status));
  const title = userInfo?.name
    ? tt("a.8a622865", "{{v0}} 파트너님", { v0: userInfo.name })
    : tt("a.b4f21c8e", "파트너센터");
  // 상태 카드가 계정 진입점 (R2) — 내 정보·비밀번호 변경 경로 중 어느 하나에 있으면 카드를 켠다
  const accountActive = PARTNER_ACCOUNT_MENU.some((item) => isPartnerMenuActive(location.pathname, item));
  // 크리에이터 겸 파트너에게만 「크리에이터 홈으로 →」 (2026-09-28 대표 결정 「파트너도 크리에이터처럼」). Admin 은 띠에 같은 링크가 있다
  const showCreatorHome = showsCreatorHomeDoor(userInfo?.role); // 판정은 partnerNav 한 곳(상단 스트립·홈 카드와 같다)

  return (
    <Box
      as="nav"
      aria-label={tt("a.4cf7e7bf", "파트너 메뉴")}
      display={{ base: "none", md: "block" }}
      w={`${PARTNER_RAIL_W}px`} flex="none"
      bg="white" borderRight="1px solid" borderColor="neutral.200"
      position="sticky" top={`${PARTNER_STRIP_H}px`} alignSelf="flex-start"
      maxH={`calc(100dvh - ${PARTNER_STRIP_H}px)`} overflowY="auto"
      px={2} py={4}
      className="admin-scroll"
    >
      {/* 상태 카드 = 계정 진입점 (R2) — 누르면 내 정보(프로필 · 보안 · 언어) */}
      <Box
        as="button" type="button" w="calc(100% - 8px)" textAlign="left" mx={1}
        border="1px solid" borderColor={accountActive ? "brand.300" : "neutral.200"} bg={accountActive ? "brand.50" : "white"}
        borderRadius="8px" px={3} py={2.5} fontSize="xs" color="neutral.600" transition="all 0.15s ease"
        _hover={{ borderColor: "brand.300", bg: "brand.50" }}
        aria-current={accountActive ? "page" : undefined}
        aria-label={tt("a.f6c42c77", "내 정보")}
        onClick={() => navigate("/edit-profile")}
      >
        <Text fontSize="sm" fontWeight="700" color="neutral.800" noOfLines={1}>{title}</Text>
        <HStack spacing={1} mt={1} flexWrap="wrap">
          {/* 유형 이름이 가장 길면(「영상권리사 · 크리에이터 파트너 (확인 전)」) 레일 카드 폭을 넘는다 — 배지 안에서 줄을 바꾼다(2026-10-26) */}
          <Badge colorScheme={typeBadge.scheme} variant="subtle" whiteSpace="normal" textAlign="left" maxW="100%">{typeBadge.label}</Badge>
          {stageMeta && <Badge colorScheme={stageMeta.scheme} variant="subtle">{stageMeta.label}</Badge>}
        </HStack>
        {showCode && <Text fontFamily="mono" mt={1} color="neutral.700">{program.partner_code}</Text>}
        <Flex align="center" mt={1.5} fontSize="11px" fontWeight="700" color="brand.700">
          {tt("a.f6c42c77", "내 정보")} · {tt("a.109cc06d", "설정")} <ChevronRightIcon boxSize={3.5} />
        </Flex>
      </Box>

      <SectionLabel>{tt("a.5bc9d189", "메뉴")}</SectionLabel>
      <Box display="grid" gap="2px" px={1}>
        {menu.map((item) => (
          <RailItem key={item.key} item={item} active={isPartnerMenuActive(location.pathname, item)} onClick={() => navigate(item.path)} />
        ))}
      </Box>

      {/* 크리에이터 홈으로 — 겸직만. 상단 스트립 버튼과 같은 목적지 */}
      {showCreatorHome && (
        <Flex
          as="button" type="button" w="calc(100% - 8px)" mx={1} mt={4}
          align="center" justify="center" gap={1.5} px={3} py={2} borderRadius="8px"
          border="1px solid" borderColor="brand.200" bg="brand.50" color="brand.700" fontSize="sm" fontWeight="700"
          _hover={{ bg: "brand.100" }}
          data-track="파트너 레일 크리에이터 홈"
          onClick={() => navigate("/home")}
        >
          {tt("a.partnerCreator.railLink", "크리에이터 홈으로 →")}
        </Flex>
      )}

      {/* 담당자 */}
      <Box border="1px solid" borderColor="neutral.200" borderRadius="8px" px={3} py={2.5} mx={1} mt={4} fontSize="xs" color="neutral.600">
        <Text fontWeight="700" color="neutral.800" fontSize="xs">{tt("a.9ff7e209", "담당자")}</Text>
        <Text mt={1} color="neutral.700">{tt("a.de263822", "레이블리 파트너팀")}</Text>
        <Text wordBreak="break-all">{tt("a.9334c4de", "카카오톡 채널 · contact@laeebly.com")}</Text>
      </Box>
    </Box>
  );
}
